import React, { ReactElement, useContext } from 'react';

import Resource, { ResourceProps } from '../Resource';
import filterPropsByComponent from '../../../utils/components/_filterPropsByComponent';
import SchemaContext from '../../schema/_SchemaContext';

export interface ResourceResolverProps<Data, Error = {}> extends Omit<ResourceProps<Data, Error>, 'children'> {
  /** A function getting the resourceSrc */
  children: (renderProps: ResourceMapperRenderProps) => ReactElement;
}

interface ResourceMapperRenderProps {
  resourceSrc: string;
}

export interface ResourceMapperRenderStatics {
  publicProps: (keyof ResourceResolverProps<any>)[];
}

/**
 * @since 0.21.0
 */
const ResourceResolver = Object.assign(
  <Data, Error = {}> ({ children, name, url, dataDst, resourceSrc, ...props }: ResourceResolverProps<Data, Error>) => {
    const { schema } = useContext(SchemaContext);

    if (!resourceSrc) {
      const currentDataDst = (
        dataDst ||
        (
          name ?
            schema.getDataDst({ name }) :
            ({ resourceId }: { resourceId: number }) => (
              schema.decorateComponent('resource', { ...props, dataDst, resourceId }).dataDst as string
            )
        )
      );

      return (
        <Resource<Data, Error>
          name={name}
          url={url}
          dataDst={currentDataDst}
          {...filterPropsByComponent(props, Resource<Data, Error>)}
        >
          {({ name: resourceName }) => children({ resourceSrc: resourceName })}
        </Resource>
      );
    }

    const { name: fixedResourceSrc } = schema.decorateComponent('resource', { name: resourceSrc });

    return children({ resourceSrc: fixedResourceSrc });
  },
  {
    publicProps: ['resourceSrc', ...Resource.publicProps],
  },
);

export default ResourceResolver;
